if (!customElements.get('resource-lead-form')) {
  customElements.define(
    'resource-lead-form',
    class ResourceLeadForm extends HTMLElement {
      /* Compartida por TODAS las instancias: email -> promesa de lead-status.
         Hay un formulario por printable (52 en la página) y todos leen el
         mismo email de localStorage, así que sin esto una sola apertura
         disparaba decenas de peticiones idénticas. Se vacía tras cada envío,
         porque el `downloadCount` cambia. */
      static cachePaso = new Map();

      constructor() {
        super();
        this.captureUtmParams();

        this.form = this.querySelector('form');
        this.errorMessage = this.querySelector('.resource-lead-form__message--error');
        this.successPanel = this.querySelector('.resource-lead-form__success');
        this.downloadLink = this.querySelector('.resource-lead-form__download');
        this.submitButton = this.querySelector('.resource-lead-form__submit');
        this.spinner = this.submitButton ? this.submitButton.querySelector('.loading__spinner') : null;

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        this.initSelectPlaceholders();
        this.initProgressiveSteps();
        this.initGruposOpciones();
      }

      /* Grupos de opciones del cuestionario real (2026-09-18):

         - `data-fr-max`  -> tope de selección ("Select up to two"): al llegar
           al límite se deshabilitan las casillas sin marcar, en vez de dejar
           marcar y luego rechazar con un error.
         - `data-fr-otro-toggle` -> la opción "Other" despliega su campo de
           texto y lo hace obligatorio; al desmarcarla se oculta y se limpia.
         - `data-fr-required` -> los grupos de CASILLAS no tienen `required`
           nativo (marcar una no satisface al resto), así que la obligación se
           valida a mano con setCustomValidity sobre la primera casilla. */
      initGruposOpciones() {
        this.querySelectorAll('[data-fr-group]').forEach((grupo) => {
          const casillas = [...grupo.querySelectorAll('input[type="checkbox"], input[type="radio"]')];
          const max = Number(grupo.dataset.frMax || 0);
          const requerido = grupo.dataset.frRequired === 'true';
          const otroToggle = grupo.querySelector('[data-fr-otro-toggle]');
          const otroInput = grupo.querySelector('[data-fr-otro-input]');
          const primera = casillas[0];

          const sincronizar = () => {
            const marcadas = casillas.filter((c) => c.checked);

            // Tope: bloquear las no marcadas al llegar al máximo.
            if (max > 0) {
              const tope = marcadas.length >= max;
              casillas.forEach((c) => {
                c.disabled = tope && !c.checked;
                c.closest('.fr-option')?.classList.toggle('is-disabled', c.disabled);
              });
            }

            // "Other": mostrar u ocultar su campo de texto.
            if (otroToggle && otroInput) {
              const activo = otroToggle.checked;
              otroInput.hidden = !activo;
              otroInput.required = activo;
              if (!activo) otroInput.value = '';
              else otroInput.focus({ preventScroll: true });
            }

            // Obligatorio en grupos de casillas: sin esto se podría enviar
            // el paso sin marcar nada.
            if (requerido && primera && primera.type === 'checkbox') {
              primera.setCustomValidity(marcadas.length ? '' : 'Please select at least one option.');
            }

            grupo.classList.toggle('is-complete', marcadas.length > 0);
          };

          casillas.forEach((c) => c.addEventListener('change', sincronizar));
          sincronizar();
        });
      }

      /* Formulario progresivo: al salir del campo de email se consulta cuántas
         descargas lleva esa persona y se muestra el paso 2 o el 3 si toca.
         Se usa `blur` y no `input` para no lanzar una petición por tecla.

         Si el endpoint falla o no está desplegado todavía, no pasa nada: el
         formulario se queda en el paso 1 y la descarga funciona igual. */
      initProgressiveSteps() {
        const emailInput = this.querySelector('input[type="email"]');
        if (!emailInput) return;

        // Los pasos 2 y 3 nacen ocultos: hay que desactivar sus campos ya, o
        // un `required` invisible impediría enviar el paso 1.
        this.querySelectorAll('[data-fr-step][hidden]').forEach((b) => {
          b.querySelectorAll('input, select, textarea').forEach((c) => { c.disabled = true; });
        });

        const endpoint = this.dataset.statusEndpoint;
        if (!endpoint) return;

        // Si esta persona ya descargó antes desde este navegador, se consulta
        // su estado al abrir — sin esperar a que escriba nada.
        //
        // Mientras llega la respuesta se OCULTA el formulario: si no, se ve el
        // paso 1 y un instante después salta al paso que toca, o directamente
        // a la descarga. Ese parpadeo es lo que hacía que el cambio de
        // formulario no se sintiera fluido.
        // La consulta se hace CADA VEZ que el modal se abre, no solo al
        // construir el elemento: el modal existe en el HTML desde que carga la
        // página, así que el constructor corre antes de que la persona haya
        // descargado nada. Sin esto, quien descarga y vuelve a abrir sin
        // recargar seguiría viendo el paso 1.
        this.alAbrir = () => {
          const guardado = this.emailRecordado();
          if (!guardado || guardado === this.ultimoConsultado) return;
          this.ultimoConsultado = guardado;
          emailInput.value = guardado;
          this.mostrarCargando(true);
          this.consultarPaso(guardado);
        };

        const modal = this.closest('modal-dialog, .quick-add-modal');
        if (modal && window.MutationObserver) {
          // El modal de Dawn se abre añadiendo el atributo `open`.
          new MutationObserver(() => {
            if (modal.hasAttribute('open')) this.alAbrir();
          }).observe(modal, { attributes: true, attributeFilter: ['open'] });
        }

        // Y también al construir, por si el modal ya estaba abierto.
        this.alAbrir();

        emailInput.addEventListener('blur', () => {
          const email = emailInput.value.trim().toLowerCase();
          if (!email || email === this.ultimoConsultado) return;
          if (!emailInput.checkValidity()) return;
          this.ultimoConsultado = email;
          this.consultarPaso(email);
        });
      }

      /* Oculta el formulario mientras se decide qué paso mostrar, para que no
         se vea el paso 1 y un instante después otro distinto. */
      mostrarCargando(activo) {
        if (this.form) this.form.style.visibility = activo ? 'hidden' : '';

        let aviso = this.querySelector('[data-fr-loading]');
        if (activo && !aviso) {
          aviso = document.createElement('p');
          aviso.setAttribute('data-fr-loading', '');
          aviso.className = 'resource-lead-form__loading';
          aviso.textContent = 'One moment…';
          this.insertBefore(aviso, this.firstChild);
        } else if (!activo && aviso) {
          aviso.remove();
        }
      }

      /* Pregunta al backend qué paso toca para este email y lo aplica.

         La respuesta se CACHEA por email y la promesa se COMPARTE entre todas
         las instancias (ver `ResourceLeadForm.cachePaso`). Motivo: hay un
         `resource-lead-form` por printable —con 52 en la página, antes cada
         uno consultaba por su cuenta y una sola apertura disparaba 15+ GET
         idénticos al backend. Con 3 printables no se notaba; con 52 sí.

         La caché se invalida tras cada envío, porque `downloadCount` cambia
         y el siguiente modal debe ver el paso nuevo. */
      consultarPaso(email) {
        const endpoint = this.dataset.statusEndpoint;
        if (!endpoint) return;

        const cache = ResourceLeadForm.cachePaso;
        let pendiente = cache.get(email);
        if (!pendiente) {
          pendiente = fetch(`${endpoint}?email=${encodeURIComponent(email)}`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
          cache.set(email, pendiente);
        }

        pendiente
          .then((data) => {
            if (!data || !data.ok) return;
            // El email vive en el paso 1; al ocultarlo su campo queda
            // `disabled` y no se enviaría. Se copia a un hidden que sobrevive
            // al cambio de paso, porque el backend lo necesita para
            // identificar al Customer.
            if (Number(data.nextFormStep) !== 1) this.preservarEmail(email);
            this.mostrarPaso(data.nextFormStep);
          })
          .catch(() => {
            /* sin conexión o endpoint caído: se queda en el paso 1 */
          })
          .finally(() => {
            // Se quita SIEMPRE, también si la consulta falló: si no, el
            // formulario quedaría invisible para siempre.
            this.mostrarCargando(false);
          });
      }

      /* El email de quien ya descargó antes desde este navegador. Se guarda
         al completar una descarga y permite reconocer a la persona sin
         pedirle nada — necesario para la descarga directa de la 4ª en
         adelante. Si el navegador bloquea localStorage, se degrada solo: sin
         email recordado, el formulario se comporta como con alguien nuevo. */
      emailRecordado() {
        try {
          return localStorage.getItem('canticos_lead_email') || null;
        } catch (e) {
          return null;
        }
      }

      recordarEmail(email) {
        try {
          localStorage.setItem('canticos_lead_email', email);
        } catch (e) {
          /* modo incógnito o almacenamiento bloqueado: no es crítico */
        }
      }

      /* Copia el email a un input oculto fuera de los pasos, para que siga
         viajando en el POST aunque el paso 1 quede oculto y deshabilitado. */
      preservarEmail(email) {
        let hidden = this.querySelector('input[data-fr-email-keep]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'email';
          hidden.setAttribute('data-fr-email-keep', '');
          this.form.appendChild(hidden);
        }
        hidden.value = email;
      }

      /* Muestra un paso y oculta los demás: los 3 pasos se SUSTITUYEN entre
         sí, no se suman. Si el paso pedido no existe (p. ej. el cliente aún
         no cargó las preguntas del 2), se deja el paso 1 para que la descarga
         siga funcionando.

         Los campos de un paso oculto se marcan `disabled`: si no, un `required`
         invisible bloquearía el envío con un error que el usuario no puede
         ver ni corregir. `disabled` además los excluye del FormData, así que
         solo se envían las respuestas del paso que se rellenó. */
      mostrarPaso(paso) {
        const bloques = [...this.querySelectorAll('[data-fr-step]')];
        if (!bloques.length) return;

        // Paso 0 = ya respondió las tres tandas: no se le pide nada más y se
        // envía el formulario solo, con el email recordado. La descarga
        // arranca sin que tenga que tocar nada.
        if (Number(paso) === 0) {
          this.descargaDirecta();
          return;
        }

        const objetivo = bloques.some((b) => Number(b.dataset.frStep) === Number(paso))
          ? Number(paso)
          : 1;

        bloques.forEach((b) => {
          const visible = Number(b.dataset.frStep) === objetivo;
          b.hidden = !visible;
          b.querySelectorAll('input, select, textarea').forEach((campo) => {
            campo.disabled = !visible;
          });

          // Al reactivar el paso visible se reactivaban TAMBIÉN las casillas
          // que el tope de "Select up to two" había bloqueado, y el campo de
          // texto de "Other" que estaba oculto. Se vuelve a aplicar la regla
          // de cada grupo para que el estado quede coherente.
          if (visible) {
            b.querySelectorAll('[data-fr-group]').forEach((g) => {
              const cs = [...g.querySelectorAll('input[type="checkbox"], input[type="radio"]')];
              const max = Number(g.dataset.frMax || 0);
              if (max > 0) {
                const tope = cs.filter((c) => c.checked).length >= max;
                cs.forEach((c) => {
                  c.disabled = tope && !c.checked;
                  c.closest('.fr-option')?.classList.toggle('is-disabled', c.disabled);
                });
              }
              const toggle = g.querySelector('[data-fr-otro-toggle]');
              const texto = g.querySelector('[data-fr-otro-input]');
              if (toggle && texto) {
                texto.hidden = !toggle.checked;
                texto.required = toggle.checked;
                texto.disabled = false;
              }
            });
          }
        });
      }

      /* 4ª descarga en adelante: la persona ya respondió las tres tandas, así
         que no se le piden datos otra vez. Se ocultan los campos y queda solo
         el botón de descarga.

         IMPORTANTE: NO se descarga sola. Abrir el modal no es pedir el
         archivo, y una descarga sin clic además la bloquean los navegadores.
         El envío al backend ocurre al pulsar, para no perder el registro de
         qué printable se lleva cada persona. */
      descargaDirecta() {
        const email = this.emailRecordado();
        if (!email) return; // sin email no se puede identificar: se deja el paso 1

        this.querySelectorAll('[data-fr-step]').forEach((b) => {
          b.hidden = true;
          b.querySelectorAll('input, select, textarea').forEach((c) => { c.disabled = true; });
        });

        this.preservarEmail(email);

        // Un mensaje corto antes del botón: sin él el modal queda con un
        // botón suelto y no se entiende por qué no piden datos esta vez.
        if (!this.querySelector('[data-fr-directo]')) {
          const aviso = document.createElement('div');
          aviso.setAttribute('data-fr-directo', '');
          aviso.className = 'resource-lead-form__directo';
          aviso.innerHTML =
            '<h2 class="resource-info__title">Welcome back!</h2>' +
            '<p class="resource-info__hint">You’re all set — your printable is ready to download.</p>';
          this.form.insertBefore(aviso, this.form.firstChild);
        }

        // Un solo botón, sin campos: la persona pulsa y se descarga.
        if (this.submitButton) {
          this.submitButton.hidden = false;
          const etiqueta = this.submitButton.querySelector('span');
          if (etiqueta) etiqueta.textContent = 'Download again';
        }
        this.modoDirecto = true;
      }

      /* Marca los <select> vacíos con `data-fr-empty` para que el CSS pinte
         el placeholder más claro que un valor ya elegido. Sin esto, "Month"
         y el mes seleccionado se verían igual. */
      initSelectPlaceholders() {
        const selects = this.querySelectorAll('.fr-select select');
        selects.forEach((sel) => {
          const sync = () => sel.toggleAttribute('data-fr-empty', !sel.value);
          sync();
          sel.addEventListener('change', sync);
        });
      }

      captureUtmParams() {
        const params = new URLSearchParams(window.location.search);
        const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
        const hasUtm = utmKeys.some((key) => params.has(key));

        if (hasUtm) {
          const utmData = {};
          utmKeys.forEach((key) => {
            if (params.has(key)) utmData[key] = params.get(key);
          });
          utmData.landing_url = window.location.href;
          utmData.referrer = document.referrer || '';
          sessionStorage.setItem('canticos_utm', JSON.stringify(utmData));
        }
      }

      getTrackingData() {
        try {
          const stored = sessionStorage.getItem('canticos_utm');
          if (stored) return JSON.parse(stored);
        } catch (e) {
          // ignore malformed sessionStorage data
        }
        return { landing_url: window.location.href, referrer: document.referrer || '' };
      }

      onSubmit(event) {
        event.preventDefault();

        const formData = new FormData(this.form);
        const website = formData.get('website');
        formData.delete('website');

        // Los campos de opción múltiple llegan como `clave[]` repetida. Con
        // `data[key] = value` solo sobrevivía la última marcada, así que se
        // juntan en un array y se guardan bajo la clave SIN los corchetes.
        // El "Other" se resuelve aquí: el valor centinela `__otro__` se
        // sustituye por lo que la persona escribió en el campo de texto.
        const data = {};
        const multiples = {};
        formData.forEach((value, key) => {
          if (key.endsWith('[]')) {
            const limpio = key.slice(0, -2);
            (multiples[limpio] = multiples[limpio] || []).push(value);
          } else {
            data[key] = value;
          }
        });

        Object.entries(multiples).forEach(([key, valores]) => {
          const texto = (data[`${key}_otro`] || '').trim();
          const resueltos = valores
            .map((v) => (v === '__otro__' ? texto : v))
            .filter(Boolean);
          // Separador ` | ` y NO `, `: varias opciones llevan comas dentro
          // ("Games, puzzles, or learning flash cards"), así que al separar
          // por coma el dashboard las contaba como tres respuestas distintas.
          data[key] = resueltos.join(' | ');
          delete data[`${key}_otro`];
        });

        // Lo mismo para los de opción ÚNICA con "Other" (radios): el valor
        // enviado es `__otro__` y hay que cambiarlo por el texto escrito.
        Object.keys(data).forEach((key) => {
          if (key.endsWith('_otro')) return;
          if (data[key] === '__otro__') {
            const texto = (data[`${key}_otro`] || '').trim();
            data[key] = texto || 'Other';
          }
        });
        Object.keys(data).forEach((key) => {
          if (key.endsWith('_otro')) delete data[key];
        });

        // En modo directo (4ª descarga en adelante) los campos están
        // desactivados, así que el FormData llega casi vacío: se completa con
        // el email recordado para que el backend identifique a la persona, y
        // se marca el consentimiento —que ya dio en su primera descarga— para
        // que el registro quede consistente.
        if (this.modoDirecto) {
          if (!data.email) {
            const guardado = this.emailRecordado();
            if (guardado) data.email = guardado;
          }
          if (!data.consentimiento) data.consentimiento = 'true';
        }

        // Los desplegables de mes y día del selector de fecha existen solo por
        // fidelidad al diseño: se pide la fecha completa pero se guarda ÚNICAMENTE
        // el año (decisión del cliente, 2026-09-04). Se descartan aquí para que no
        // acaben en el JSON de `datos` del metaobject `solicitud_descarga`.
        Object.keys(data).forEach((key) => {
          if (key.endsWith('_month') || key.endsWith('_day')) delete data[key];
        });

        this.enviar(data, website);
      }

      /* Envía al backend y arranca la descarga. Lo usan tanto el submit normal
         como la descarga directa de la 4ª descarga en adelante. */
      enviar(data, website) {
        this.setLoading(true);
        this.hideError();

        fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resourceHandle: this.dataset.resourceHandle,
            formData: data,
            website,
            tracking: this.getTrackingData(),
          }),
        })
          .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
          .then(({ ok, json }) => {
            if (!ok || !json.ok) {
              throw new Error(json.error || 'No se pudo procesar la solicitud');
            }
            // Se recuerda el email para reconocer a esta persona en la próxima
            // descarga sin volver a preguntárselo.
            if (data.email) this.recordarEmail(String(data.email).trim().toLowerCase());

            // Esta descarga cambió el `downloadCount`, así que la respuesta
            // cacheada de lead-status quedó obsoleta: el siguiente modal que
            // se abra debe volver a preguntar o mostraría el paso anterior.
            // También se limpia el guard por instancia de los OTROS
            // formularios, que si no se saltarían la consulta.
            ResourceLeadForm.cachePaso.clear();
            document.querySelectorAll('resource-lead-form').forEach((f) => {
              f.ultimoConsultado = null;
            });

            this.showSuccess(json.downloadUrl);
          })
          .catch((error) => {
            this.showError(error.message);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.submitButton.disabled = isLoading;
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = message || 'Ocurrió un error, intenta de nuevo.';
        this.errorMessage.hidden = false;
      }

      hideError() {
        if (!this.errorMessage) return;
        this.errorMessage.hidden = true;
      }

      showSuccess(downloadUrl) {
        if (this.form) this.form.hidden = true;
        if (this.successPanel) this.successPanel.hidden = false;
        if (this.downloadLink && downloadUrl) {
          this.downloadLink.href = downloadUrl;
        }

        this.launchConfetti();

        // La descarga arranca sola: la persona ya pidió el printable, no debe
        // tener que pulsar otra vez. Se retrasa un poco para que se vea el
        // confeti antes de que el navegador abra el diálogo de guardado.
        // El enlace del panel sigue ahí por si el navegador la bloquea.
        if (downloadUrl) {
          setTimeout(() => this.triggerDownload(downloadUrl), 700);
        }
      }

      /* El PDF se abre en una PESTAÑA NUEVA (2026-09-18, petición del
         cliente): así quien descarga no pierde la página de printables y
         puede seguir bajando más.

         `download` NO sirve aquí: el PDF vive en `cdn.shopify.com`, otro
         dominio, y los navegadores IGNORAN ese atributo en enlaces
         cross-origin. Por eso antes navegaba al PDF en la misma pestaña en
         vez de guardarlo. Con `target="_blank"` el visor se abre aparte y la
         página de origen queda intacta.

         `noopener` evita que la pestaña nueva pueda tocar `window.opener`. */
      triggerDownload(url) {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      /* Confeti en canvas, sin librerías: el CSP del tema bloquea scripts
         externos y para unas partículas no compensa cargar una dependencia.
         Se dibuja sobre el panel de éxito y se autodestruye al terminar. */
      launchConfetti() {
        const host = this.successPanel || this;
        if (!host) return;

        // Respetar a quien pide menos movimiento por accesibilidad.
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const canvas = document.createElement('canvas');
        canvas.className = 'resource-lead-form__confetti';
        host.appendChild(canvas);

        const rect = host.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;

        const ctx = canvas.getContext('2d');
        if (!ctx) { canvas.remove(); return; }
        ctx.scale(dpr, dpr);

        const colores = ['#FC6BA5', '#4A5CA7', '#FFCD34', '#45C1CD', '#FF004F'];
        const originX = rect.width / 2;
        const originY = rect.height * 0.72; // sale desde la zona del botón
        const particulas = [];

        for (let i = 0; i < 70; i++) {
          const ang = (-Math.PI / 2) + (Math.random() - 0.5) * 2.1;
          const vel = 4 + Math.random() * 6;
          particulas.push({
            x: originX, y: originY,
            vx: Math.cos(ang) * vel,
            vy: Math.sin(ang) * vel,
            w: 5 + Math.random() * 5,
            h: 3 + Math.random() * 4,
            rot: Math.random() * Math.PI,
            vr: (Math.random() - 0.5) * 0.3,
            color: colores[i % colores.length],
            vida: 1,
          });
        }

        let frames = 0;
        const tick = () => {
          frames++;
          ctx.clearRect(0, 0, rect.width, rect.height);
          let vivas = 0;

          for (const p of particulas) {
            p.vy += 0.16;      // gravedad
            p.vx *= 0.99;      // rozamiento
            p.x += p.vx;
            p.y += p.vy;
            p.rot += p.vr;
            if (frames > 40) p.vida -= 0.025;
            if (p.vida <= 0 || p.y > rect.height + 20) continue;
            vivas++;

            ctx.save();
            ctx.globalAlpha = Math.max(p.vida, 0);
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            ctx.fillStyle = p.color;
            ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
            ctx.restore();
          }

          if (vivas > 0 && frames < 260) {
            requestAnimationFrame(tick);
          } else {
            canvas.remove();
          }
        };
        requestAnimationFrame(tick);
      }
    }
  );
}
